import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, of, catchError
} from "rxjs";
import { map } from "rxjs/operators";
import {
    MarketArea, MarketAreaSearchFilterOptions
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    ArrayUtil, StringUtil
} from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { MarketAreaApiClientService } from "../../iapi-application/services/api-client/market-area/market-area-api-client.service";
import { IMarketAreaAutocompleteOptions } from "./interfaces/market-area-autocomplete-options";

/**
 * Autocomplete controller for MarketArea entities
 * Provides autocomplete functionality with chain filtering, backed by the v2 `MarketAreaApiClientService`
 *
 * **Service Type:** Autocomplete Controller Service
 *
 * **Provided:** Root-level singleton
 */
@Injectable({
    providedIn: "root"
})
export class MarketAreaAutocompleteControllerService implements IAutocompleteController<MarketArea, IMarketAreaAutocompleteOptions> {

    private readonly _marketAreaClient = inject(MarketAreaApiClientService);

    /**
     * Get autocomplete suggestions for market areas based on query and options
     * Always shows options on focus, uses client-side filtering
     */
    autocomplete$(query?: string, options?: IMarketAreaAutocompleteOptions): Observable<IAutocompleteResults<MarketArea>> {
        // Return empty results if no chainId is provided (required by API)
        if (!options?.chainId) {
            return of({
                options: []
            });
        }

        // Build the search filter for the client
        const filter = new MarketAreaSearchFilterOptions();
        filter.chainId = options.chainId;

        // Use custom sort if provided, otherwise default to alphabetical by name (ascending)
        const sort: Array<ISearchSort> = options?.sort ?? [{
            field: "name",
            direction: SearchSortDirection.Ascending
        }];

        return this._marketAreaClient.getAll$(filter, sort).pipe(
            map((marketAreas) => {
                let filteredMarketAreas = marketAreas;

                // Apply query filter (client-side)
                if (!StringUtil.emptyString(query)) {
                    const queryLower = StringUtil.sanitizeString(query).toLowerCase();
                    filteredMarketAreas = marketAreas.filter((marketArea) =>
                        marketArea.name?.toLowerCase().includes(queryLower));
                }

                // Convert market areas to autocomplete options
                const autocompleteOptions: Array<IAutocompleteOption<MarketArea>> = filteredMarketAreas.map((marketArea: MarketArea) => ({
                    option: marketArea.name || "",
                    value: marketArea
                }));

                // Add orphaned stores option if requested
                if (options?.showOrphans) {
                    autocompleteOptions.unshift({
                        option: "Orphaned Stores",
                        value: MarketArea.deserialize({
                            id: "-1",
                            name: "Orphaned Stores"
                        })
                    });
                }

                return {
                    options: autocompleteOptions
                };
            }),
            catchError(() => {
                // Return empty results on error
                return of({
                    options: []
                });
            })
        );
    }

    /**
     * Get display name for a market area in autocomplete
     */
    getAutocompleteDisplayName(value?: MarketArea, options?: IMarketAreaAutocompleteOptions): string {
        return value?.name || "";
    }

    /**
     * Returns a unique identifier for tracking items in the autocomplete
     */
    trackByIdentifier(index: number, result: IAutocompleteOption<MarketArea>): any {
        return result?.value?.id || index;
    }

    /**
     * IAutocompleteController function to determine if the item is unique compared to the items in the array
     * @param value
     * @param items Array of MarketAreas
     * @returns true if the value is not in the items array, false otherwise
     */
    isUnique(value: MarketArea, items: Array<MarketArea>): boolean {
        return !ArrayUtil.contains(value, items);
    }
}
